import { CaretRightOutlined, MessageOutlined } from '@ant-design/icons';
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  MutationDefinition,
} from '@reduxjs/toolkit/dist/query';
import { MutationTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { Button, Popconfirm, Space, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { selectUser } from '../../../../../auth/states/userSlice';
import { setCommonModal } from '../../../../../common/slices/modalSlice';
import {
  HTTPResponse,
  IPermission,
  ISetState,
  crud,
} from '../../../../../common/types/commonTypes';
import { formatAmount, formatAmountAbs } from '../../../../../common/utils/common.utils';
import { dateFormate } from '../../../../Dashboard/Utils/formateDate';
import { useDeleteClientMutation } from '../../api/endpoints/clientEndpoints';
import { ClientDataType } from '../../types/clientInterfaces';
import ClientFollowUp from './ClientFollowUp';

type TActivate = MutationTrigger<
  MutationDefinition<
    {
      client_id: number;
      status: 'active' | 'inactive';
      created_by: number;
    },
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, {}>,
    string,
    HTTPResponse<void>,
    'AccountsApi'
  >
>;

type TClientColumns = {
  setEditInfo: ISetState<ClientDataType | null>;
  showModal: () => void;
  activate: TActivate;
  loading: boolean;
  permission?: IPermission;
  checkButtonState?: boolean;
  pagination: {
    current: number;
    pageSize: number;
  };
};

export const viewClientcolumns = ({
  activate,
  loading,
  setEditInfo,
  showModal,
  permission,
  pagination,
}: TClientColumns): ColumnsType<ClientDataType> => {
  const [columnIndex, setColumnIndex] = useState<number | null>(null);
  const dispatch = useAppDispatch();

  const handleClick = (record: ClientDataType) => {
    setEditInfo(record);
  };
  const user = useAppSelector(selectUser);
  const [deleteClient, { isLoading, isSuccess, isError }] = useDeleteClientMutation();

  const handleClientDelete = async (values: any, index: number) => {
    setColumnIndex(index);
    await deleteClient({
      ...values,
      client_updated_by: user?.user_id,
    });
  };
  useEffect(() => {
    if (isSuccess) {
      setColumnIndex(null);
    } else if (isError) {
      setColumnIndex(null);
    }
  }, [isSuccess, isError]);

  return [
    {
      title: 'Sl.',
      render: (_, data, index) => (
        <>{((pagination.current || 1) - 1) * (pagination.pageSize || 20) + 1 + index}</>
      ),
    },

    {
      title: 'Category',
      dataIndex: 'category_prefix',
      key: 'category_prefix',
    },
    {
      title: 'ID',
      dataIndex: 'client_entry_id',
      key: 'client_entry_id',
    },
    {
      title: 'Type',
      dataIndex: 'client_type',
      key: 'client_type',
    },
    {
      title: 'Policy',
      dataIndex: 'client_payment_policy',
      key: 'client_payment_policy',
    },
    {
      title: 'Name',
      dataIndex: 'client_name',
      key: 'client_name',

      render: (_, { client_name, client_id }) => {
        return <Link to={`/reports/client_ledger?client_query=${client_id}`}>{client_name}</Link>;
      },
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      key: 'mobile',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },

    {
      title: 'Relationship manager',
      dataIndex: 'contacted_person',
      key: 'contacted_person',
    },
    {
      title: 'Feedback',
      dataIndex: 'feedback',
      key: 'feedback',
      render: (text) =>
        text?.length > 30 ? <Tooltip title={text}>{text?.substring(0, 50)}...</Tooltip> : text,
    },
    {
      title: 'Present Due/Advance',
      dataIndex: 'client_last_balance',
      key: 'client_last_balance',
      align: 'right',
      render: (_, record) => {
        const { client_last_balance } = record;

        return (
          <>
            {client_last_balance != undefined && client_last_balance > 0 && (
              <span className='color-profit'>{formatAmount(client_last_balance)}</span>
            )}
            {client_last_balance == 0 && formatAmount(client_last_balance)}
            {client_last_balance != undefined && client_last_balance < 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  type='text'
                  size='small'
                  onClick={() => {
                    handleClick(record);
                    showModal();
                  }}
                >
                  <MessageOutlined />
                </Button>
                <span className='color-due'>{formatAmountAbs(client_last_balance)}</span>
              </div>
            )}
          </>
        );
      },
    },

    {
      title: 'LQ Sales',
      align: 'right',
      render: (_, record) => {
        return <>{record?.last_quarter_sales}</>;
      },
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
    },
    {
      title: 'Last Trans.',
      dataIndex: 'ctrxn_created_date',
      key: 'ctrxn_created_date',
      render: (curr) => {
        return curr ? dateFormate(curr) : null;
      },
    },

    {
      title: 'Action',
      key: 'action',

      render: (_, record, index) => (
        <Space size='small'>
          {permission?.['read:any'] && (
            <>
              {record?.contacted_person ? (
                record?.follow_up_status !== 'done' ? (
                  <Button
                    iconPosition='end'
                    icon={<CaretRightOutlined />}
                    size='small'
                    color='primary'
                    type='primary'
                    onClick={() =>
                      dispatch(
                        setCommonModal({
                          content: <ClientFollowUp data={record as any} />,
                          show: true,
                          title: 'Client Follow Up',
                          width: 520,
                        })
                      )
                    }
                  >
                    {record?.follow_up_status ? record?.follow_up_status : 'Follow Up'}
                  </Button>
                ) : (
                  <Button
                    size='small'
                    type='primary'
                    style={{ backgroundColor: '#0F766E', border: 'none' }}
                  >
                    Done
                  </Button>
                )
              ) : (
                <Tooltip title='Please add Relationship Manager first'>
                  <Button size='small' type='primary' disabled style={{ cursor: 'not-allowed' }}>
                    Follow Up
                  </Button>
                </Tooltip>
              )}
            </>
          )}
          {permission?.['read:any'] && (
            <Link to={`/clients/details/client-${record.client_id}`} state={location.pathname}>
              <Button size='small' type='primary'>
                <Typography style={{ color: '#ffffff' }}>View</Typography>
              </Button>
            </Link>
          )}
          {permission?.[crud.update] && (
            <Link to={`/clients/edit/client-${record.client_id}`} state={location.pathname}>
              <Button size='small' type='primary'>
                <Typography style={{ color: '#ffffff' }}>Edit </Typography>
              </Button>
            </Link>
          )}

          {(permission?.[crud.delete] &&
            // record.trxn_count == 0 &&
            record.client_last_balance == 0.0) ||
          record?.client_last_balance === undefined ||
          record.client_last_balance === null ? (
            <Popconfirm title='Sure to delete?' onConfirm={() => handleClientDelete(record, index)}>
              <Button danger size='small' type='primary' loading={columnIndex === index && true}>
                <Typography style={{ color: '#fff' }}>Delete </Typography>
              </Button>
            </Popconfirm>
          ) : (
            // <Button disabled danger size='small' type='primary'>
            //   <Typography>Delete </Typography>
            // </Button>
            ''
          )}
        </Space>
      ),
    },
  ];
};
